import { ChevronLeft, ChevronRight, Download, Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui";

interface CarouselLightboxProps {
  urls: string[];
  onClose: () => void;
  onDownload: (url: string) => Promise<void>;
}

export function CarouselLightbox({ urls, onClose, onDownload }: CarouselLightboxProps) {
  const [idx, setIdx] = useState(0);
  const [showGrid, setShowGrid] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min(urls.length - 1, i + 1));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [urls.length, onClose]);

  const handleDownloadClick = async (url: string) => {
    setIsDownloading(true);
    await onDownload(url);
    setIsDownloading(false);
  };

  if (showGrid) {
    return (
      <div
        key="grid"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="relative max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-semibold text-lg text-zinc-900">
              Photo Carousel ({urls.length} photos)
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowGrid(false)}>
                Slideshow
              </Button>
              <Button
                size="icon"
                variant="secondary"
                onClick={onClose}
                className="rounded-full"
              >
                <X />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {urls.map((url, i) => (
              <button
                key={i}
                onClick={() => {
                  setIdx(i);
                  setShowGrid(false);
                }}
                className="overflow-hidden rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <img
                  src={url}
                  alt={`Photo ${i + 1}`}
                  className="w-full object-cover transition-transform hover:scale-105"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      key="carousel"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      <div
        className="relative flex h-full w-full max-w-5xl flex-col items-center justify-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar */}
        <div className="absolute top-4 right-4 left-4 flex items-center justify-between">
          <span className="rounded-full bg-secondary px-3 py-1 font-medium text-secondary-foreground text-sm">
            {idx + 1} / {urls.length}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowGrid(true)}>
              Grid
            </Button>
            <Button
              onClick={() => handleDownloadClick(urls[idx])}
              disabled={isDownloading}
              size="icon"
              variant="secondary"
              className="rounded-full"
            >
              {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
            </Button>
            <Button
              size="icon"
              variant="secondary"
              onClick={onClose}
              className="rounded-full"
            >
              <X />
            </Button>
          </div>
        </div>

        {/* Main image */}
        <img
          src={urls[idx]}
          alt={`Photo ${idx + 1}`}
          className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
        />

        {/* Nav arrows */}
        {idx > 0 && (
          <Button
            size="icon"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setIdx(idx - 1);
            }}
            className="absolute top-1/2 left-2 -translate-y-1/2 rounded-full active:not-aria-[haspopup]:-translate-y-1/2 sm:left-6"
          >
            <ChevronLeft className="size-6" />
          </Button>
        )}
        {idx < urls.length - 1 && (
          <Button
            size="icon"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              setIdx(idx + 1);
            }}
            className="absolute top-1/2 right-2 -translate-y-1/2 rounded-full active:not-aria-[haspopup]:-translate-y-1/2 sm:right-6"
          >
            <ChevronRight className="size-6" />
          </Button>
        )}

        {/* Thumbnail strip */}
        <div className="no-scrollbar mt-4 flex w-full overflow-x-auto p-1">
          <div className="mx-auto flex gap-2">
            {urls.map((url, i) => (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  setIdx(i);
                }}
                className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                  i === idx
                    ? "scale-110 border-white shadow-lg"
                    : "border-transparent opacity-60 hover:opacity-100"
                }`}
              >
                <img
                  src={url}
                  alt={`Thumb ${i + 1}`}
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
